import React, { useState, useEffect } from 'react';
import { Brain, Zap, Shield, Save, RotateCcw, Info, AlertTriangle, Eye, Link, Cpu, RefreshCw, CheckCircle, XCircle, AlertCircle, Database, TrendingUp, Clock, Activity, Loader2 } from 'lucide-react';
import { useSettings, SettingsData } from '../contexts/SettingsContext';
import { useToast } from '../contexts/ToastContext';
import { AI_PROVIDERS, PROVIDER_DISPLAY_NAMES, getModelDisplayName } from '../utils/ai-providers';
import { modelsService, ModelInfo } from '../services/models';
import { FrontendProviderAvailabilityChecker, getProviderStatusMessage } from '../utils/provider-availability';
import { ProviderStatus } from '../components/ProviderStatus';
import { AutoscalingSettings } from '../components/AutoscalingSettings';
import { api } from '../utils/api';

export function Settings() {
  const {
    settings,
    updateSettings,
    saveSettings: saveSettingsToStore,
    resetSettings: resetSettingsToDefaults,
    isLoading,
    isSaving,
    error
  } = useSettings();

  const { showToast } = useToast();
  const [availableModels, setAvailableModels] = useState<Record<string, ModelInfo[]>>({});
  const [providerAvailability, setProviderAvailability] = useState<Record<string, boolean>>({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [availabilityChecker, setAvailabilityChecker] = useState<FrontendProviderAvailabilityChecker | null>(null);
  const [providerHealth, setProviderHealth] = useState<Record<string, any>>({});
  const [isFlushingQueue, setIsFlushingQueue] = useState(false);
  const [flushResult, setFlushResult] = useState<{
    success: boolean;
    message: string;
    flushed_count: number;
    levels_flushed?: string[];
    stats_before?: any;
    total_remaining?: number;
  } | null>(null);

  const [cleanupWarning, setCleanupWarning] = useState<{ field: string; newVal: number } | null>(null);
  const [cleanupEstimate, setCleanupEstimate] = useState<any>(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isSavingPartial, setIsSavingPartial] = useState(false);

  // Load available models on component mount
  useEffect(() => {
    loadAvailableModels();
    loadProviderStatus();
  }, []);

  const showCleanupWarning = async (field: string, newVal: number) => {
    setCleanupWarning({ field, newVal });
    setIsEstimating(true);
    setCleanupEstimate(null);

    try {
      // Use standard fetch through pages function
      const response = await fetch(`/api/v1/diagnostics/cleanup-estimate?days=${newVal}`, {
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setCleanupEstimate(data.data.estimate);
      }
    } catch (err) {
      console.error('Failed to get cleanup estimate:', err);
    } finally {
      setIsEstimating(false);
    }
  };

  const confirmCleanupChange = () => {
    if (cleanupWarning) {
      updateSettings({ [cleanupWarning.field]: cleanupWarning.newVal });
      setCleanupWarning(null);
    }
  };

  // Refresh provider status when relevance provider changes
  useEffect(() => {
    if (settings.relevanceAiProvider) {
      loadProviderStatus();
    }
  }, [settings.relevanceAiProvider]);

  const loadAvailableModels = async () => {
    setLoadingModels(true);
    setModelsError(null);

    try {
      // Clear cache to ensure fresh data
      modelsService.clearCache();

      // Load provider availability from backend
      const backendAvailability = await modelsService.getProviderAvailability();
      setProviderAvailability(backendAvailability);

      // Create availability checker with API keys (simulated from backend availability)
      const apiKeys: Record<string, string | undefined> = {};
      Object.entries(backendAvailability).forEach(([provider, available]) => {
        if (available) {
          // Mark as configured if backend says it's available
          apiKeys[`${provider.toUpperCase()}_API_KEY`] = 'configured';
        }
      });

      const checker = new FrontendProviderAvailabilityChecker(apiKeys);
      setAvailabilityChecker(checker);

      // Load models for all providers in parallel, handling errors gracefully
      const models: Record<string, ModelInfo[]> = {};
      const modelPromises = Object.values(AI_PROVIDERS).map(async (provider) => {
        try {
          const providerModels = await modelsService.getModelsForProvider(provider);
          models[provider] = providerModels;
        } catch (error) {
          console.error(`Failed to load models for ${provider}:`, error);
          // Use fallback models for failed providers
          models[provider] = await modelsService.getAvailableModels(provider);
        }
      });

      // Wait for all providers to complete (success or failure)
      await Promise.allSettled(modelPromises);

      setAvailableModels(models);
    } catch (error) {
      console.error('Failed to load models:', error);
      setModelsError('Some providers could not be loaded. Using fallback data.');

      // Load fallback models for all providers
      const fallbackModels: Record<string, ModelInfo[]> = {};
      for (const provider of Object.values(AI_PROVIDERS)) {
        fallbackModels[provider] = await modelsService.getAvailableModels(provider);
      }
      setAvailableModels(fallbackModels);
    } finally {
      setLoadingModels(false);
    }
  };

  const refreshModels = async () => {
    setLoadingModels(true);
    try {
      await modelsService.refreshModelCache();
      await loadAvailableModels();
      showToast('Models refreshed successfully!', 'success');
    } catch (error) {
      console.error('Failed to refresh models:', error);
      setModelsError('Failed to refresh models.');
      showToast('Failed to refresh models', 'error');
    } finally {
      setLoadingModels(false);
    }
  };

  const saveSettings = async () => {
    try {
      await saveSettingsToStore();
      showToast('Settings saved successfully!', 'success');
    } catch (error) {
      console.error('Failed to save settings:', error);
      showToast('Failed to save settings. Please try again.', 'error');
    }
  };

  const savePartialSettings = async (updates: Partial<SettingsData>, message: string) => {
    if (isSavingPartial) return; // Prevent double submissions
    
    setIsSavingPartial(true);
    
    // Merge updates with current settings
    const newSettings = { ...settings, ...updates };
    
    // Update React state immediately (optimistic update)
    updateSettings(updates);
    
    // Update localStorage immediately to prevent race conditions on page reload
    try {
      localStorage.setItem('osm-threat-intel-settings', JSON.stringify(newSettings));
    } catch (localStorageError) {
      console.warn('Failed to update localStorage:', localStorageError);
    }
    
    try {
      // Save to API
      await api.saveSettings(newSettings as any);
      showToast(message, 'success');
    } catch (error) {
      console.error('Failed to save settings:', error);
      showToast('Failed to save settings. Please try again.', 'error');
      // Revert both React state and localStorage on error
      updateSettings(settings);
      try {
        localStorage.setItem('osm-threat-intel-settings', JSON.stringify(settings));
      } catch (revertError) {
        console.warn('Failed to revert localStorage:', revertError);
      }
    } finally {
      setIsSavingPartial(false);
    }
  };

  const handleDepthChange = async (depth: number) => {
    savePartialSettings(
      { analysisDepth: depth },
      `URL Analysis Depth updated to L${depth - 1}`
    );
  };

  const resetSettings = () => {
    resetSettingsToDefaults();
    showToast('Settings reset to defaults', 'info');
  };

  const loadProviderStatus = async () => {
    try {
      // Use service binding through pages function
      const response = await fetch(`/api/v1/providers/status`, {
        headers: {
          'Content-Type': 'application/json'
          // No API key needed - authentication handled by service binding
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.providers) {
          setProviderHealth(data.data.providers);
        }
      } else if (response.status === 401) {
        console.log('Provider status endpoint requires authentication');
      }
    } catch (error) {
      console.error('Failed to load provider status:', error);
      // Don't show error to user - this is not critical for the page to function
    }
  };

  const triggerHealthCheck = async (provider?: string) => {
    try {
      console.log('Triggering health check for provider:', provider);
      showToast('Running health check...', 'info');

      // Use service binding through pages function
      const response = await fetch(`/api/v1/providers/health-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
          // No API key needed - authentication handled by service binding
        },
        body: JSON.stringify({
          provider,
          forceCheck: true
        })
      });

      console.log('Health check response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Health check response data:', data);

        if (data.success && data.data) {
          // Update provider health with new data
          setProviderHealth(prev => ({
            ...prev,
            ...data.data
          }));
          showToast(`Health check completed${provider ? ` for ${provider}` : ' for all providers'}`, 'success');
        } else {
          console.error('Health check failed:', data);
          showToast(data.error || 'Health check failed', 'error');
        }
      } else {
        const errorText = await response.text();
        console.error('Health check HTTP error:', response.status, errorText);
        showToast(`Health check failed: ${response.status} ${response.statusText}`, 'error');
      }
    } catch (error) {
      console.error('Failed to trigger health check:', error);
      showToast(`Failed to perform health check: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  const performManualFlush = async (forceFlushAll: boolean = false) => {
    setIsFlushingQueue(true);
    setFlushResult(null);

    try {
      const data = await api.manualFlush(forceFlushAll);

      // The API client already unwraps the response, so data is the actual data object
      setFlushResult({
        success: true,
        message: data.message,
        flushed_count: data.flushed_count,
        levels_flushed: data.levels_flushed,
        stats_before: data.stats_before,
        total_remaining: data.total_remaining
      });

      // Clear result after 10 seconds
      setTimeout(() => {
        setFlushResult(null);
      }, 10000);
    } catch (error) {
      console.error('Failed to perform manual flush:', error);
      setFlushResult({
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        flushed_count: 0
      });
    } finally {
      setIsFlushingQueue(false);
    }
  };

  const handleAnalysisModeChange = (mode: 'existing' | 'advanced') => {
    savePartialSettings(
      {
        aiAnalysisMode: mode,
        // Auto-enable OCR and multi-modal when switching to advanced
        ocrEnabled: mode === 'advanced' ? true : settings.ocrEnabled,
        multiModalAnalysis: mode === 'advanced' ? true : settings.multiModalAnalysis
      },
      `Switched to ${mode === 'advanced' ? 'Advanced' : 'Default'} AI Analysis`
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                AI Analysis Settings
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure AI analysis modes and features for threat intelligence processing
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* AI Analysis Mode Selection */}
            <div>
              <label className="text-base font-medium text-gray-900">
                AI Analysis Mode
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Choose between existing analysis or advanced multi-modal analysis with OCR
              </p>

              <div className="space-y-4">
                {/* Existing Analysis Option */}
                <div className="relative">
                  <div className="flex items-center">
                    <input
                      id="existing-analysis"
                      name="ai-analysis-mode"
                      type="radio"
                      checked={settings.aiAnalysisMode === 'existing'}
                      onChange={() => handleAnalysisModeChange('existing')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="existing-analysis" className="ml-3 block text-sm font-medium text-gray-700">
                      <div className="flex items-center">
                        <Brain className="h-4 w-4 mr-2 text-blue-600" />
                        Default AI Analysis
                      </div>
                    </label>
                  </div>
                  <p className="ml-7 text-sm text-gray-500">
                    Uses {getModelDisplayName(settings.aiModel || '@cf/meta/llama-3.1-8b-instruct')} for text analysis and DistilBERT for sentiment classification
                  </p>
                </div>

                {/* Advanced Analysis Option */}
                <div className="relative">
                  <div className="flex items-center">
                    <input
                      id="advanced-analysis"
                      name="ai-analysis-mode"
                      type="radio"
                      checked={settings.aiAnalysisMode === 'advanced'}
                      onChange={() => handleAnalysisModeChange('advanced')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="advanced-analysis" className="ml-3 block text-sm font-medium text-gray-700">
                      <div className="flex items-center">
                        <Zap className="h-4 w-4 mr-2 text-purple-600" />
                        Advanced AI Analysis
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          New
                        </span>
                      </div>
                    </label>
                  </div>
                  <p className="ml-7 text-sm text-gray-500">
                    Multi-modal analysis with OCR using Mistral Pixtral for image and document processing
                  </p>
                </div>
              </div>
            </div>

            {/* AI Provider Selection - Only for Default AI Analysis */}
            {settings.aiAnalysisMode === 'existing' && (
              <div className="border-l-4 border-blue-400 pl-4 space-y-4">
                <div>
                  <label className="text-base font-medium text-gray-900 flex items-center">
                    <Cpu className="h-5 w-5 mr-2 text-blue-600" />
                    AI Provider & Model Selection
                  </label>
                  <p className="text-sm text-gray-500 mb-4">
                    Choose your preferred AI provider and model for threat analysis
                  </p>
                </div>

                {/* Provider Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="ai-provider" className="block text-sm font-medium text-gray-700">
                      AI Provider
                    </label>
                    <button
                      onClick={refreshModels}
                      disabled={loadingModels}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${loadingModels ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                  <select
                    id="ai-provider"
                    value={settings.aiProvider}
                    onChange={(e) => {
                      const newProvider = e.target.value as any;
                      const providerModels = availableModels[newProvider] || [];
                      const defaultModel = providerModels.length > 0 ? providerModels[0].id : '';
                      savePartialSettings({
                        aiProvider: newProvider,
                        aiModel: defaultModel
                      }, 'AI Provider updated');
                    }}
                    disabled={isSavingPartial || loadingModels}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {Object.entries(AI_PROVIDERS).map(([key, value]) => (
                      <option key={value} value={value}>
                        {PROVIDER_DISPLAY_NAMES[value]}
                        {providerAvailability && providerAvailability[value] === false ? ' (Not Configured)' : ''}
                      </option>
                    ))}
                  </select>
                  {modelsError && (
                    <p className="mt-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">{modelsError}</p>
                  )}
                </div>

                {/* Model Selection */}
                <div>
                  <label htmlFor="ai-model" className="block text-sm font-medium text-gray-700 mb-2">
                    AI Model
                    {loadingModels && (
                      <span className="ml-2 text-xs text-gray-500">(Loading...)</span>
                    )}
                  </label>
                  <select
                    id="ai-model"
                    value={settings.aiModel}
                    onChange={(e) => savePartialSettings({ aiModel: e.target.value }, 'AI Model updated')}
                    disabled={loadingModels || isSavingPartial}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {(availableModels[settings.aiProvider] || []).map((model) => (
                      <option key={model.id} value={model.id} title={model.description}>
                        {model.name}
                        {model.context_length && ` (${model.context_length.toLocaleString()} tokens)`}
                      </option>
                    ))}
                  </select>
                  {/* Show notice if provider is not configured */}
                  {providerAvailability && providerAvailability[settings.aiProvider] === false && (
                    <p className="mt-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      This provider is not configured. Using fallback model list.
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {(availableModels[settings.aiProvider] || []).find(m => m.id === settings.aiModel)?.description ||
                      'Different models have varying capabilities and cost implications'}
                  </p>

                  {/* Model Capabilities */}
                  {(() => {
                    const selectedModel = (availableModels[settings.aiProvider] || []).find(m => m.id === settings.aiModel);
                    if (selectedModel?.capabilities && selectedModel.capabilities.length > 0) {
                      return (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {selectedModel.capabilities.map((capability) => (
                            <span
                              key={capability}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                            >
                              {capability}
                            </span>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Fallback Provider Selection */}
                <div>
                  <label htmlFor="ai-fallback-provider" className="block text-sm font-medium text-gray-700 mb-2">
                    Fallback AI Provider
                    <span className="ml-1 text-xs text-gray-500">(Used if primary provider fails)</span>
                  </label>
                  <select
                    id="ai-fallback-provider"
                    value={settings.aiFallbackProvider}
                    onChange={(e) => {
                      const newFallbackProvider = e.target.value;
                      const fallbackProviderModels = newFallbackProvider !== 'none' ? availableModels[newFallbackProvider] || [] : [];
                      const defaultFallbackModel = fallbackProviderModels.length > 0 ? fallbackProviderModels[0].id : '';
                      savePartialSettings({
                        aiFallbackProvider: newFallbackProvider as any,
                        aiFallbackModel: defaultFallbackModel
                      }, 'Fallback Provider configuration saved');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="none">No Fallback</option>
                    {Object.entries(AI_PROVIDERS).map(([key, value]) => {
                      // Don't allow selecting the same provider as primary
                      if (value === settings.aiProvider) return null;
                      return (
                        <option key={value} value={value}>
                          {PROVIDER_DISPLAY_NAMES[value]}
                          {providerAvailability && providerAvailability[value] === false ? ' (Unavailable)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Fallback Model Selection */}
                {(settings.aiFallbackProvider as string) !== 'none' && (
                  <div>
                    <label htmlFor="ai-fallback-model" className="block text-sm font-medium text-gray-700 mb-2">
                      Fallback AI Model
                      {loadingModels && (
                        <span className="ml-2 text-xs text-gray-500">(Loading...)</span>
                      )}
                    </label>
                    <select
                      id="ai-fallback-model"
                      value={settings.aiFallbackModel}
                      onChange={(e) => savePartialSettings({ aiFallbackModel: e.target.value }, 'Fallback Model updated')}
                      disabled={loadingModels || settings.aiFallbackProvider === 'none'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50"
                    >
                      {(availableModels[settings.aiFallbackProvider] || []).map((model) => (
                        <option key={model.id} value={model.id} title={model.description}>
                          {model.name}
                          {model.context_length && ` (${model.context_length.toLocaleString()} tokens)`}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {(availableModels[settings.aiFallbackProvider] || []).find(m => m.id === settings.aiFallbackModel)?.description ||
                        'Fallback model to use when primary provider fails'}
                    </p>

                    {/* Fallback Model Capabilities */}
                    {(() => {
                      const selectedFallbackModel = (availableModels[settings.aiFallbackProvider] || []).find(m => m.id === settings.aiFallbackModel);
                      if (selectedFallbackModel?.capabilities && selectedFallbackModel.capabilities.length > 0) {
                        return (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {selectedFallbackModel.capabilities.map((capability) => (
                              <span
                                key={capability}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800"
                              >
                                {capability}
                              </span>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {/* Provider Status Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex">
                    <Info className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium">Provider Status</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {Object.entries(providerAvailability || {}).map(([provider, available]) => {
                          const availability = availabilityChecker?.getProviderAvailability(provider);
                          return (
                            <div key={provider} className="flex items-center">
                              {available ? (
                                <CheckCircle className="h-3 w-3 text-green-500 mr-2" />
                              ) : (
                                <AlertCircle className="h-3 w-3 text-gray-400 mr-2" />
                              )}
                              <span className="text-xs">
                                {PROVIDER_DISPLAY_NAMES[provider as keyof typeof PROVIDER_DISPLAY_NAMES]}
                                <span className="text-gray-500 ml-1">
                                  ({available ? 'Configured' : 'Not configured'})
                                </span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-xs">
                        Providers with configured API keys are marked as available.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Analysis Features */}
            {settings.aiAnalysisMode === 'advanced' && (
              <div className="border-l-4 border-purple-400 pl-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="ocr-enabled" className="text-sm font-medium text-gray-700">
                      OCR Processing
                    </label>
                    <p className="text-sm text-gray-500">
                      Extract text from images, screenshots, and documents
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="ocr-enabled"
                      type="checkbox"
                      checked={settings.ocrEnabled}
                      onChange={(e) => savePartialSettings({
                        ocrEnabled: e.target.checked
                      }, 'OCR settings saved')}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="multimodal-analysis" className="text-sm font-medium text-gray-700">
                      Multi-Modal Analysis
                    </label>
                    <p className="text-sm text-gray-500">
                      Combine text, image, and document analysis for comprehensive threat assessment
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="multimodal-analysis"
                      type="checkbox"
                      checked={settings.multiModalAnalysis}
                      onChange={(e) => savePartialSettings({
                        multiModalAnalysis: e.target.checked
                      }, 'Multi-modal analysis settings saved')}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confidence-threshold" className="text-sm font-medium text-gray-700">
                    Confidence Threshold: {settings.confidenceThreshold}
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Minimum confidence score for OCR text extraction (0.1 - 1.0)
                  </p>
                  <input
                    id="confidence-threshold"
                    type="range"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={settings.confidenceThreshold}
                    onChange={(e) => savePartialSettings({
                      confidenceThreshold: parseFloat(e.target.value)
                    }, 'Confidence threshold updated')}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                </div>

                <div className="pt-6 border-t border-purple-100 dark:border-purple-900/30 mt-6 space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-400 mb-1 flex items-center">
                      <Cpu className="w-4 h-4 mr-2" />
                      Advanced Model Configuration
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Select AI providers and specific models for each stage of the advanced analysis pipeline.
                    </p>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Advanced Text Analysis */}
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"></div>
                        <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Text Analysis</span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Provider</label>
                          <select
                            value={settings.advancedTextProvider || 'cloudflare'}
                            onChange={(e) => savePartialSettings({ advancedTextProvider: e.target.value as any }, 'Advanced text provider updated')}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm text-gray-900 dark:text-gray-100"
                          >
                            {Object.entries(PROVIDER_DISPLAY_NAMES).map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Model</label>
                          <select
                            value={settings.advancedTextModel || ''}
                            onChange={(e) => savePartialSettings({ advancedTextModel: e.target.value }, 'Advanced text model updated')}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm text-gray-900 dark:text-gray-100"
                          >
                            {(availableModels[settings.advancedTextProvider || 'cloudflare'] || []).map((model) => (
                              <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                            {!(availableModels[settings.advancedTextProvider || 'cloudflare'] || []).find(m => m.id === settings.advancedTextModel) && (
                              <option value={settings.advancedTextModel}>{settings.advancedTextModel}</option>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Advanced OCR Analysis */}
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"></div>
                        <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">OCR / Image-to-Text</span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Provider</label>
                          <select
                            value={settings.advancedOcrProvider || 'cloudflare'}
                            onChange={(e) => savePartialSettings({ advancedOcrProvider: e.target.value as any }, 'Advanced OCR provider updated')}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm text-gray-900 dark:text-gray-100"
                          >
                            {Object.entries(PROVIDER_DISPLAY_NAMES).map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Model</label>
                          <select
                            value={settings.advancedOcrModel || ''}
                            onChange={(e) => savePartialSettings({ advancedOcrModel: e.target.value }, 'Advanced OCR model updated')}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm text-gray-900 dark:text-gray-100"
                          >
                            {(availableModels[settings.advancedOcrProvider || 'cloudflare'] || []).map((model) => (
                              <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                            {!(availableModels[settings.advancedOcrProvider || 'cloudflare'] || []).find(m => m.id === settings.advancedOcrModel) && (
                              <option value={settings.advancedOcrModel}>{settings.advancedOcrModel}</option>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Advanced Vision Analysis */}
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"></div>
                        <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Vision / Classification</span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Provider</label>
                          <select
                            value={settings.advancedVisionProvider || 'cloudflare'}
                            onChange={(e) => savePartialSettings({ advancedVisionProvider: e.target.value as any }, 'Advanced vision provider updated')}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm text-gray-900 dark:text-gray-100"
                          >
                            {Object.entries(PROVIDER_DISPLAY_NAMES).map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Model</label>
                          <select
                            value={settings.advancedVisionModel || ''}
                            onChange={(e) => savePartialSettings({ advancedVisionModel: e.target.value }, 'Advanced vision model updated')}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm text-gray-900 dark:text-gray-100"
                          >
                            {(availableModels[settings.advancedVisionProvider || 'cloudflare'] || []).map((model) => (
                              <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                            {!(availableModels[settings.advancedVisionProvider || 'cloudflare'] || []).find(m => m.id === settings.advancedVisionModel) && (
                              <option value={settings.advancedVisionModel}>{settings.advancedVisionModel}</option>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/50 rounded p-3 text-xs text-purple-800 dark:text-purple-300">
                    <p className="flex items-center">
                      <Info className="w-3 h-3 mr-2" />
                      These models are used only when "Advanced AI Analysis" mode is selected. You can now choose between different AI providers for each step.
                    </p>
                  </div>
                </div>

              </div>
            )}

            {/* Information Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <Info className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800">
                    Analysis Mode Information
                  </h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        <strong>Existing Analysis:</strong> Fast, text-based analysis suitable for high-volume processing
                      </li>
                      <li>
                        <strong>Advanced Analysis:</strong> Comprehensive multi-modal analysis with OCR capabilities for images and documents
                      </li>
                      <li>
                        Changes take effect immediately for new posts being analyzed
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Analysis Performance Configuration */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center mb-6">
                <Cpu className="h-6 w-6 text-purple-600 mr-3" />
                <div>
                  <h4 className="text-base font-medium text-gray-900">
                    AI Analysis Performance
                  </h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure parallel processing for AI analysis tasks
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label htmlFor="ai-analysis-concurrency" className="text-base font-medium text-gray-900">
                    Parallel AI Analysis Workers: {settings.aiAnalysisConcurrency || 5}
                  </label>
                  <p className="text-sm text-gray-500 mb-4">
                    Number of simultaneous AI API calls per worker (1-10)
                  </p>

                  <div className="space-y-4">
                    <input
                      id="ai-analysis-concurrency"
                      type="range"
                      min="1"
                      max="10"
                      step="1"
                      value={settings.aiAnalysisConcurrency || 5}
                      onChange={(e) => savePartialSettings({
                        aiAnalysisConcurrency: parseInt(e.target.value)
                      }, 'Analysis concurrency updated')}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />

                    <div className="flex justify-between text-sm text-gray-600">
                      <span>1 (Slow)</span>
                      <span>5 (Default)</span>
                      <span>10 (Fast)</span>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                    <h5 className="text-sm font-medium text-purple-800 mb-3">Performance Impact:</h5>
                    <div className="space-y-2 text-sm text-purple-700">
                      <div className="flex items-start space-x-3">
                        <span className="font-bold text-purple-900">Lower (1-3):</span>
                        <span>Reduced API costs, slower processing, better for rate-limited APIs</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <span className="font-bold text-purple-900">Medium (4-6):</span>
                        <span>Balanced performance and cost, recommended for most use cases</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <span className="font-bold text-purple-900">Higher (7-10):</span>
                        <span>Faster processing, higher API costs, may hit rate limits on some providers</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex">
                      <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-yellow-800">
                          Important Note
                        </h4>
                        <p className="mt-1 text-sm text-yellow-700">
                          This setting controls internal concurrency within each Cloudflare Queue worker.
                          The actual number of queue consumers is configured at deployment time.
                          Higher values may result in increased API costs and potential rate limiting.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Relevance Scoring AI Provider Configuration */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <Brain className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Advanced Relevance Scoring (Primary Method)
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure AI provider for content relevance and threat scoring
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="text-base font-medium text-gray-900 flex items-center">
                <Cpu className="h-5 w-5 mr-2 text-green-600" />
                Relevance Scoring AI Provider
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Choose which AI provider analyzes posts to determine relevance and threat level
              </p>
            </div>

            {/* Provider Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="relevance-ai-provider" className="block text-sm font-medium text-gray-700">
                  AI Provider
                </label>
                <button
                  onClick={loadProviderStatus}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded hover:bg-green-100"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Check Status
                </button>
              </div>
              <select
                id="relevance-ai-provider"
                value={settings.relevanceAiProvider ?? 'anthropic'}
                onChange={(e) => {
                  const newProvider = e.target.value as any;
                  const providerModels = availableModels[newProvider] || [];
                  const defaultModel = providerModels.length > 0 ? providerModels[0].id : '';
                  savePartialSettings({
                    relevanceAiProvider: newProvider,
                    relevanceAiModel: defaultModel
                  }, 'Relevance AI Provider updated');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
              >
                {Object.entries(AI_PROVIDERS).map(([key, value]) => (
                  <option key={value} value={value}>
                    {PROVIDER_DISPLAY_NAMES[value]}
                    {providerAvailability[value] === false ? ' (Unavailable)' : ''}
                  </option>
                ))}
              </select>

              {/* Provider Status Indicator */}
              {settings.relevanceAiProvider && providerHealth[settings.relevanceAiProvider] && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 mr-2">Provider Status:</span>
                      {(() => {
                        const health = providerHealth[settings.relevanceAiProvider];
                        const status = health.status;

                        if (status === 'operational') {
                          return (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                              <span className="text-sm text-green-600 font-medium">Operational</span>
                            </>
                          );
                        } else if (status === 'degraded') {
                          return (
                            <>
                              <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
                              <span className="text-sm text-yellow-600 font-medium">Degraded</span>
                            </>
                          );
                        } else if (status === 'failed') {
                          return (
                            <>
                              <XCircle className="w-4 h-4 text-red-500 mr-1" />
                              <span className="text-sm text-red-600 font-medium">Failed</span>
                            </>
                          );
                        } else {
                          return (
                            <>
                              <AlertCircle className="w-4 h-4 text-gray-500 mr-1" />
                              <span className="text-sm text-gray-600 font-medium">Not Configured</span>
                            </>
                          );
                        }
                      })()}
                    </div>
                    {providerHealth[settings.relevanceAiProvider].health_check?.response_time && (
                      <span className="text-xs text-gray-500">
                        {providerHealth[settings.relevanceAiProvider].health_check.response_time}ms
                      </span>
                    )}
                  </div>

                  {/* Show error message if provider is failing */}
                  {providerHealth[settings.relevanceAiProvider].health_check?.error_message && (
                    <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                      <div className="flex items-start">
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm text-red-700">
                          <p className="font-medium">Health Check Failed:</p>
                          <p className="text-xs mt-0.5">{providerHealth[settings.relevanceAiProvider].health_check.error_message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Manual health check button */}
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={() => triggerHealthCheck(settings.relevanceAiProvider)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded hover:bg-blue-100 transition-colors"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Test Provider Now
                    </button>
                  </div>

                  {/* Show fallback information */}
                  {providerHealth[settings.relevanceAiProvider].relevance_scoring && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>24h Relevance Analyses:</span>
                          <span className="font-medium">{providerHealth[settings.relevanceAiProvider].relevance_scoring.total_analyses || 0}</span>
                        </div>
                        {providerHealth[settings.relevanceAiProvider].relevance_scoring.fallback_usage > 0 && (
                          <div className="flex justify-between text-orange-600">
                            <span>Fallback to Cloudflare AI:</span>
                            <span className="font-medium">{providerHealth[settings.relevanceAiProvider].relevance_scoring.fallback_usage} times</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Model Selection */}
            <div>
              <label htmlFor="relevance-ai-model" className="block text-sm font-medium text-gray-700 mb-2">
                AI Model
              </label>
              <select
                id="relevance-ai-model"
                value={settings.relevanceAiModel ?? ''}
                onChange={(e) => savePartialSettings({ relevanceAiModel: e.target.value }, 'Relevance AI Model updated')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
              >
                {(availableModels[settings.relevanceAiProvider ?? 'anthropic'] || []).map((model) => (
                  <option key={model.id} value={model.id} title={model.description}>
                    {model.name}
                    {model.context_length && ` (${model.context_length.toLocaleString()} tokens)`}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {(availableModels[settings.relevanceAiProvider ?? 'anthropic'] || []).find(m => m.id === settings.relevanceAiModel)?.description ||
                  'Different models have varying capabilities for threat assessment'}
              </p>

              {/* Model Capabilities */}
              {(() => {
                const selectedModel = (availableModels[settings.relevanceAiProvider ?? 'anthropic'] || []).find(m => m.id === settings.relevanceAiModel);
                if (selectedModel?.capabilities && selectedModel.capabilities.length > 0) {
                  return (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedModel.capabilities.map((capability) => (
                        <span
                          key={capability}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                        >
                          {capability}
                        </span>
                      ))}
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Fallback Provider Configuration */}
            <div className="pt-6 mt-6 border-t border-gray-200">
              <h4 className="text-base font-medium text-gray-900 flex items-center mb-4">
                <Shield className="h-5 w-5 mr-2 text-orange-600" />
                Fallback Provider Configuration
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Configure a fallback provider when the primary provider is unavailable
              </p>

              {/* Fallback Provider Selection */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="relevance-fallback-provider" className="block text-sm font-medium text-gray-700 mb-2">
                    Fallback AI Provider
                  </label>
                  <select
                    id="relevance-fallback-provider"
                    value={settings.relevanceFallbackProvider || 'cloudflare'}
                    onChange={(e) => {
                      const newProvider = e.target.value as any;
                      const providerModels = availableModels[newProvider] || [];
                      const defaultModel = providerModels.length > 0 ? providerModels[0].id : '';
                      savePartialSettings({
                        relevanceFallbackProvider: newProvider,
                        relevanceFallbackModel: defaultModel
                      }, 'Relevance Fallback Provider updated');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    {Object.entries(AI_PROVIDERS).map(([key, value]) => (
                      <option key={value} value={value}>
                        {PROVIDER_DISPLAY_NAMES[value]}
                        {providerAvailability && providerAvailability[value] === false ? ' (Not Configured)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Fallback Model Selection */}
                <div>
                  <label htmlFor="relevance-fallback-model" className="block text-sm font-medium text-gray-700 mb-2">
                    Fallback AI Model
                  </label>
                  <select
                    id="relevance-fallback-model"
                    value={settings.relevanceFallbackModel || ''}
                    onChange={(e) => savePartialSettings({ relevanceFallbackModel: e.target.value }, 'Relevance Fallback Model updated')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    {(availableModels[settings.relevanceFallbackProvider || 'cloudflare'] || []).map((model) => (
                      <option key={model.id} value={model.id} title={model.description}>
                        {model.name}
                        {model.context_length && ` (${model.context_length.toLocaleString()} tokens)`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {(availableModels[settings.relevanceFallbackProvider || 'cloudflare'] || []).find(m => m.id === settings.relevanceFallbackModel)?.description ||
                      'Fallback model used when primary provider is unavailable'}
                  </p>
                </div>

                {/* Fallback Provider Status */}
                {settings.relevanceFallbackProvider && providerHealth[settings.relevanceFallbackProvider] && (
                  <div className="mt-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 mr-2">Fallback Status:</span>
                        {(() => {
                          const health = providerHealth[settings.relevanceFallbackProvider];
                          const status = health.status;

                          if (status === 'operational') {
                            return (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                                <span className="text-sm text-green-600 font-medium">Ready</span>
                              </>
                            );
                          } else if (status === 'degraded') {
                            return (
                              <>
                                <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
                                <span className="text-sm text-yellow-600 font-medium">Degraded</span>
                              </>
                            );
                          } else if (status === 'failed') {
                            return (
                              <>
                                <XCircle className="w-4 h-4 text-red-500 mr-1" />
                                <span className="text-sm text-red-600 font-medium">Failed</span>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <AlertCircle className="w-4 h-4 text-gray-500 mr-1" />
                                <span className="text-sm text-gray-600 font-medium">Not Configured</span>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Information Box */}
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mt-6">
              <div className="flex">
                <Info className="h-5 w-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">
                    About Relevance Scoring
                  </h4>
                  <div className="mt-2 text-sm text-green-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        <strong>Primary Analysis:</strong> Determines which posts are relevant for security monitoring
                      </li>
                      <li>
                        <strong>Threat Assessment:</strong> Scores posts from 0-15 based on threat intelligence value
                      </li>
                      <li>
                        <strong>Processing Impact:</strong> All posts go through relevance scoring before detailed analysis
                      </li>
                      <li>
                        <strong>Automatic Fallback:</strong> System automatically uses fallback provider when primary fails
                      </li>
                      <li>
                        Posts scoring above 0.1 (1.5/15) proceed to full AI analysis
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced AI Analysis Configuration */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <Brain className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Enhanced AI Analysis
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure when and how enhanced AI analysis runs for deeper threat assessment
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Enhanced AI Enabled Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="enhanced-ai-enabled" className="text-sm font-medium text-gray-700">
                  Enable Enhanced AI Analysis
                </label>
                <p className="text-sm text-gray-500">
                  Performs deeper analysis using dedicated AI models for advanced threat detection
                </p>
              </div>
              <div className="flex items-center">
                <input
                  id="enhanced-ai-enabled"
                  type="checkbox"
                  checked={settings.enhancedAiEnabled || false}
                  onChange={(e) => savePartialSettings({ enhancedAiEnabled: e.target.checked }, e.target.checked ? 'Enhanced AI enabled' : 'Enhanced AI disabled')}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
              </div>
            </div>

            {/* Enhanced AI Configuration - Only show if enabled */}
            {settings.enhancedAiEnabled && (
              <div className="border-l-4 border-purple-400 pl-4 space-y-6">
                {/* Trigger Configuration */}
                <div>
                  <label htmlFor="enhanced-ai-trigger" className="block text-sm font-medium text-gray-700 mb-2">
                    Analysis Trigger
                  </label>
                  <select
                    id="enhanced-ai-trigger"
                    value={settings.enhancedAiTrigger || 'high_threats'}
                    onChange={(e) => savePartialSettings({ enhancedAiTrigger: e.target.value as any }, 'Enhanced AI trigger updated')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="always">Always - Run for every post</option>
                    <option value="medium_threats_and_above">Medium Threats and Above - Run for medium, high and critical threats</option>
                    <option value="high_threats">High Threats - Run for high and critical threats only</option>
                    <option value="critical_only">Critical Only - Run only for critical threats</option>
                    <option value="never">Never - Disable enhanced analysis</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Determines when enhanced AI analysis is triggered based on initial threat assessment
                  </p>
                </div>

                {/* Enhanced AI Provider Selection */}
                <div>
                  <label htmlFor="enhanced-ai-provider" className="block text-sm font-medium text-gray-700 mb-2">
                    Enhanced AI Provider
                  </label>
                  <select
                    id="enhanced-ai-provider"
                    value={settings.enhancedAiProvider || 'anthropic'}
                    onChange={(e) => {
                      const newProvider = e.target.value as any;
                      const providerModels = availableModels[newProvider] || [];
                      const defaultModel = providerModels.length > 0 ? providerModels[0].id : '';
                      savePartialSettings({
                        enhancedAiProvider: newProvider,
                        enhancedAiModel: defaultModel
                      }, 'Enhanced AI Provider updated');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  >
                    {Object.entries(AI_PROVIDERS).map(([key, value]) => (
                      <option key={value} value={value}>
                        {PROVIDER_DISPLAY_NAMES[value]}
                        {providerAvailability && providerAvailability[value] === false ? ' (Not Configured)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    AI provider for enhanced analysis - can be different from standard analysis
                  </p>
                </div>

                {/* Enhanced AI Model Selection */}
                <div>
                  <label htmlFor="enhanced-ai-model" className="block text-sm font-medium text-gray-700 mb-2">
                    Enhanced AI Model
                  </label>
                  <select
                    id="enhanced-ai-model"
                    value={settings.enhancedAiModel || ''}
                    onChange={(e) => savePartialSettings({ enhancedAiModel: e.target.value }, 'Enhanced AI Model updated')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  >
                    {(availableModels[settings.enhancedAiProvider || 'anthropic'] || []).map((model) => (
                      <option key={model.id} value={model.id} title={model.description}>
                        {model.name}
                        {model.context_length && ` (${model.context_length.toLocaleString()} tokens)`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {(availableModels[settings.enhancedAiProvider || 'anthropic'] || []).find(m => m.id === settings.enhancedAiModel)?.description ||
                      'Advanced model for detailed threat analysis'}
                  </p>
                </div>

                {/* Enhanced AI Fallback Provider */}
                <div>
                  <label htmlFor="enhanced-ai-fallback-provider" className="block text-sm font-medium text-gray-700 mb-2">
                    Fallback Provider
                  </label>
                  <select
                    id="enhanced-ai-fallback-provider"
                    value={settings.enhancedAiFallbackProvider || 'cloudflare'}
                    onChange={(e) => {
                      const newProvider = e.target.value as any;
                      const providerModels = availableModels[newProvider] || [];
                      const defaultModel = providerModels.length > 0 ? providerModels[0].id : '';
                      savePartialSettings({
                        enhancedAiFallbackProvider: newProvider,
                        enhancedAiFallbackModel: defaultModel
                      }, 'Enhanced AI Fallback updated');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="none">No Fallback</option>
                    {Object.entries(AI_PROVIDERS).map(([key, value]) => (
                      <option key={value} value={value}>
                        {PROVIDER_DISPLAY_NAMES[value]}
                        {providerAvailability && providerAvailability[value] === false ? ' (Not Configured)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Fallback provider when enhanced AI provider fails
                  </p>
                </div>

                {/* Enhanced AI Fallback Model */}
                {(settings.enhancedAiFallbackProvider as string) !== 'none' && (
                  <div>
                    <label htmlFor="enhanced-ai-fallback-model" className="block text-sm font-medium text-gray-700 mb-2">
                      Fallback Model
                    </label>
                    <select
                      id="enhanced-ai-fallback-model"
                      value={settings.enhancedAiFallbackModel || ''}
                      onChange={(e) => savePartialSettings({ enhancedAiFallbackModel: e.target.value }, 'Enhanced AI Fallback Model updated')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    >
                      {(availableModels[settings.enhancedAiFallbackProvider] || []).map((model) => (
                        <option key={model.id} value={model.id} title={model.description}>
                          {model.name}
                          {model.context_length && ` (${model.context_length.toLocaleString()} tokens)`}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Fallback model for enhanced analysis
                    </p>
                  </div>
                )}

                {/* Information Box */}
                <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                  <div className="flex">
                    <Info className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-purple-800">
                        Enhanced AI Analysis
                      </h4>
                      <div className="mt-2 text-sm text-purple-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>
                            <strong>Deeper Analysis:</strong> Performs additional analysis with specialized models
                          </li>
                          <li>
                            <strong>Advanced IOCs:</strong> Extracts more sophisticated indicators of compromise
                          </li>
                          <li>
                            <strong>Attribution:</strong> Attempts to identify threat actors and campaigns
                          </li>
                          <li>
                            <strong>TTPs:</strong> Maps threats to MITRE ATT&CK framework
                          </li>
                          <li>
                            <strong>Resource Impact:</strong> Uses more computational resources and API calls
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* URL Analysis Depth Configuration */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <Link className="h-8 w-8 text-indigo-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                URL Analysis Depth
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure how deep the AI analysis should crawl and analyze linked content
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label htmlFor="analysis-depth" className="text-base font-medium text-gray-900 flex items-center">
                Analysis Depth Level: L{settings.analysisDepth - 1}
                {isSavingPartial && (
                  <span className="ml-3 flex items-center text-sm font-normal text-indigo-600 animate-pulse">
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    Updating...
                  </span>
                )}
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Controls how many levels of URLs the system will follow and analyze
              </p>

              <div className="space-y-4">
                <select
                  id="analysis-depth"
                  value={settings.analysisDepth}
                  onChange={(e) => handleDepthChange(parseInt(e.target.value))}
                  disabled={isSavingPartial}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value={1}>L0 - Original Post Only (fastest)</option>
                  <option value={2}>L1 - Direct URLs from posts</option>
                  <option value={3}>L2 - Nested URLs within linked pages</option>
                  <option value={4}>L3 - Deep Analysis (comprehensive)</option>
                </select>
              </div>

              <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                <h5 className="text-sm font-medium text-indigo-800 mb-3">Depth Level Guide:</h5>
                <div className="space-y-2 text-sm text-indigo-700">
                  <div className="flex items-start space-x-3">
                    <span className="font-bold text-indigo-900">L0:</span>
                    <span>Original Post - analyze only the post content and images</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="font-bold text-indigo-900">L1:</span>
                    <span>Direct URLs - crawl and analyze content from URLs mentioned in posts</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="font-bold text-indigo-900">L2:</span>
                    <span>Nested URLs - analyze URLs found within the linked pages</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="font-bold text-indigo-900">L3:</span>
                    <span>Deep Analysis - comprehensive crawling and analysis of all interconnected content</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">
                      Performance Impact
                    </h4>
                    <p className="mt-1 text-sm text-yellow-700">
                      Higher depth levels provide more comprehensive analysis but require more processing time and resources.
                      Level 4 analysis may take significantly longer for posts with many links.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Retention Configuration */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <Clock className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Data Retention Configuration
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Manage how long different types of threat intelligence data are kept in the system
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Post Retention */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="posts-retention" className="text-base font-medium text-gray-900">
                  Standard Post Retention: {settings.postsRetentionDays || 30} days
                </label>
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">Database</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Retention for unprocessed or low-threat posts from RSS feeds
              </p>
              <input
                id="posts-retention"
                type="range"
                min="1"
                max="90"
                step="1"
                value={settings.postsRetentionDays || 30}
                onChange={(e) => {
                  const newVal = parseInt(e.target.value);
                  if (newVal < (settings.postsRetentionDays || 30)) {
                    showCleanupWarning('postsRetentionDays', newVal);
                  } else {
                    updateSettings({ postsRetentionDays: newVal });
                  }
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1 day</span>
                <span>30 days</span>
                <span>60 days</span>
                <span>90 days</span>
              </div>
            </div>

            {/* Critical Retention */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="critical-retention" className="text-base font-medium text-gray-900">
                  High/Critical Threat Retention: {settings.criticalRetentionDays || 90} days
                </label>
                <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">Intelligence</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Retention for confirmed high and critical severity threat intelligence
              </p>
              <input
                id="critical-retention"
                type="range"
                min="30"
                max="365"
                step="5"
                value={settings.criticalRetentionDays || 90}
                onChange={(e) => {
                  const newVal = parseInt(e.target.value);
                  if (newVal < (settings.criticalRetentionDays || 90)) {
                    showCleanupWarning('criticalRetentionDays', newVal);
                  } else {
                    updateSettings({ criticalRetentionDays: newVal });
                  }
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>30 days</span>
                <span>90 days</span>
                <span>180 days</span>
                <span>365 days</span>
              </div>
            </div>

            {/* R2 Retention */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="r2-retention" className="text-base font-medium text-gray-900">
                  Storage Retention (R2): {settings.r2RetentionDays || 30} days
                </label>
                <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded">Object Storage</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                How long to keep crawled HTML and images in object storage
              </p>
              <input
                id="r2-retention"
                type="range"
                min="1"
                max="90"
                step="1"
                value={settings.r2RetentionDays || 30}
                onChange={(e) => {
                  const newVal = parseInt(e.target.value);
                  if (newVal < (settings.r2RetentionDays || 30)) {
                    showCleanupWarning('r2RetentionDays', newVal);
                  } else {
                    updateSettings({ r2RetentionDays: newVal });
                  }
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1 day</span>
                <span>30 days</span>
                <span>60 days</span>
                <span>90 days</span>
              </div>
            </div>

            {/* Log Retention */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="logs-retention" className="text-base font-medium text-gray-900">
                  System Log Retention: {settings.logsRetentionDays || 30} days
                </label>
                <span className="text-xs font-medium text-gray-600 bg-gray-50 px-2 py-1 rounded">Logs</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Retention for system execution logs and AI decision history
              </p>
              <input
                id="logs-retention"
                type="range"
                min="7"
                max="90"
                step="1"
                value={settings.logsRetentionDays || 30}
                onChange={(e) => {
                  const newVal = parseInt(e.target.value);
                  if (newVal < (settings.logsRetentionDays || 30)) {
                    showCleanupWarning('logsRetentionDays', newVal);
                  } else {
                    updateSettings({ logsRetentionDays: newVal });
                  }
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>7 days</span>
                <span>30 days</span>
                <span>60 days</span>
                <span>90 days</span>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex">
                <Info className="h-5 w-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">Automatic Cleanup Notice</p>
                  <p className="mt-1">
                    Cleanup tasks run daily at 03:00 UTC. Reducing retention periods will result in permanent data deletion during the next run.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cleanup Warning Modal */}
      {cleanupWarning && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Confirm Data Deletion
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Reducing the retention period to {cleanupWarning.newVal} days will cause older data to be permanently deleted at the next cleanup run.
                    </p>
                    {isEstimating ? (
                      <div className="mt-4 flex justify-center">
                        <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
                        <span className="ml-2 text-sm text-gray-600">Estimating impact...</span>
                      </div>
                    ) : cleanupEstimate !== null ? (
                      <div className="mt-4 p-3 bg-red-50 rounded-md text-left">
                        <p className="text-xs font-bold text-red-800 uppercase tracking-wider mb-2">Estimated Impact:</p>
                        <p className="text-sm text-red-700">
                          Approximately <span className="font-bold">{cleanupEstimate.total_rows.toLocaleString()}</span> records will be deleted.
                        </p>
                        {cleanupEstimate.critical_impact > 0 && (
                          <p className="text-xs text-red-600 mt-1 font-medium">
                            ⚠️ Includes {cleanupEstimate.critical_impact} critical threat records.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={confirmCleanupChange}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                >
                  Confirm Change
                </button>
                <button
                  type="button"
                  onClick={() => setCleanupWarning(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Threat Level Configuration */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <AlertTriangle className="h-8 w-8 text-orange-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Threat Level Configuration
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure which threat levels trigger alerts and analysis
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Alert Threat Level */}
            <div>
              <label htmlFor="alert-threat-level" className="text-base font-medium text-gray-900">
                Alert Generation Threshold
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Generate security alerts for posts with this threat level or higher
              </p>
              <div className="relative">
                <select
                  id="alert-threat-level"
                  value={settings.alertThreatLevel}
                  onChange={(e) => updateSettings({
                    alertThreatLevel: e.target.value as 'medium' | 'high' | 'critical'
                  })}
                  className="mt-1 block w-full pl-4 pr-10 py-3 text-base bg-white border-2 border-gray-200 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm transition-colors duration-200 cursor-pointer appearance-none"
                >
                  <option value="medium" className="py-2">🟡 Medium and above</option>
                  <option value="high" className="py-2">🔴 High and above</option>
                  <option value="critical" className="py-2">🟣 Critical only</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Threat Level Guide:</h5>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">🟡</span>
                    <span><strong>Medium:</strong> General security concerns, vulnerabilities</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">🔴</span>
                    <span><strong>High:</strong> Active threats, malware, phishing</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">🟣</span>
                    <span><strong>Critical:</strong> Immediate threats, ransomware, zero-days</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Analysis Threat Level */}
            <div>
              <label htmlFor="analysis-threat-level" className="text-base font-medium text-gray-900">
                Analysis Processing Threshold
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Process detailed AI analysis for posts with this threat level or higher
              </p>
              <div className="relative">
                <select
                  id="analysis-threat-level"
                  value={settings.analysisThreatLevel}
                  onChange={(e) => updateSettings({
                    analysisThreatLevel: e.target.value as 'low' | 'medium' | 'high' | 'critical'
                  })}
                  className="mt-1 block w-full pl-4 pr-10 py-3 text-base bg-white border-2 border-gray-200 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm transition-colors duration-200 cursor-pointer appearance-none"
                >
                  <option value="low" className="py-2">🔵 Low and above (all posts)</option>
                  <option value="medium" className="py-2">🟡 Medium and above</option>
                  <option value="high" className="py-2">🔴 High and above</option>
                  <option value="critical" className="py-2">🟣 Critical only</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                <p>Posts below this threshold will be marked as processed but skip detailed AI analysis to save resources.</p>
              </div>
            </div>

            {/* Information Box */}
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
              <div className="flex">
                <Info className="h-5 w-5 text-orange-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-orange-800">
                    Threat Level Configuration Notes
                  </h4>
                  <div className="mt-2 text-sm text-orange-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        Alert threshold determines which posts appear in the Security Alerts section
                      </li>
                      <li>
                        Analysis threshold controls which posts receive detailed AI threat analysis
                      </li>
                      <li>
                        Lower thresholds increase coverage but may generate more noise
                      </li>
                      <li>
                        Changes apply to new posts only; existing posts are not re-evaluated
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cache Performance Metrics */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <Zap className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Cache Performance Metrics
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Real-time performance metrics for the optimized caching system
              </p>
            </div>
          </div>

          <CacheMetricsDisplay />
        </div>
      </div>

      {/* Queue Management */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <RefreshCw className="h-8 w-8 text-orange-600 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Queue Management
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Manage posts in the processing queue
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border-l-4 border-orange-400 bg-orange-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-5 w-5 text-orange-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-orange-700">
                    <strong>Manual Flush:</strong> Removes posts from the queue that don't meet the current threat level threshold.
                    Posts with threat levels below "{settings.analysisThreatLevel || 'unknown'}" will be discarded.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => performManualFlush(false)}
                disabled={isFlushingQueue}
                className="w-full inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isFlushingQueue ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Flushing Queue...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Flush Queue (Threshold-Based)
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => performManualFlush(true)}
                disabled={isFlushingQueue}
                className="w-full inline-flex justify-center items-center px-4 py-3 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isFlushingQueue ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                    Flushing All...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Force Flush ALL Unanalyzed Posts
                  </>
                )}
              </button>

              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded border">
                <p><strong>Threshold-Based:</strong> Removes posts below your current analysis threshold setting.</p>
                <p><strong>Force Flush ALL:</strong> ⚠️ Removes ALL unanalyzed posts regardless of threat level. Use with caution!</p>
              </div>
            </div>

            {flushResult && (
              <div className={`rounded-md p-4 ${flushResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {flushResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-900">
                      {flushResult.message}
                    </h3>
                    {flushResult.stats_before && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p>Flushed: {flushResult.flushed_count} posts</p>
                        <p>Threat levels removed: {flushResult.levels_flushed?.join(', ') || 'none'}</p>
                        <p>Remaining in queue: {flushResult.total_remaining} posts</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={resetSettings}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </button>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={saveSettings}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Queue Autoscaling Section */}
      <AutoscalingSettings className="mt-8" />

      {/* Provider Status Section */}
      <ProviderStatus />
    </div>
  );
}

// Cache Metrics Display Component
function CacheMetricsDisplay() {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/v1/metrics/cache', {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setMetrics(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch cache metrics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2 text-gray-600">Loading cache metrics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400 mr-2" />
          <div>
            <h4 className="text-sm font-medium text-red-800">Error loading cache metrics</h4>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={fetchMetrics}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics || !metrics.overall) {
    return (
      <div className="text-center text-gray-500">
        No cache metrics available
      </div>
    );
  }

  const hitRateColor = metrics.overall.hitRate >= 80 ? 'text-green-600' :
    metrics.overall.hitRate >= 60 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="space-y-6">
      {/* Overall Cache Performance */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Database className="h-5 w-5 mr-2 text-purple-600" />
          Overall Performance
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
            <div className="flex items-center">
              <TrendingUp className="h-6 w-6 text-purple-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Cache Hit Rate</p>
                <p className={`text-2xl font-bold ${hitRateColor}`}>
                  {metrics.overall.hitRate}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-6 w-6 text-blue-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Cache Hits</p>
                <p className="text-2xl font-bold text-blue-600">
                  {metrics.overall.hits.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
            <div className="flex items-center">
              <XCircle className="h-6 w-6 text-orange-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Cache Misses</p>
                <p className="text-2xl font-bold text-orange-600">
                  {metrics.overall.misses.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
            <div className="flex items-center">
              <Database className="h-6 w-6 text-green-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Cache Size</p>
                <p className="text-2xl font-bold text-green-600">
                  {metrics.overall.sizeMB} MB
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hourly Statistics */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-blue-600" />
          Last Hour Activity
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Cache Hits</span>
              <span className="text-lg font-bold text-blue-600">
                {metrics.hourly.cacheHits.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Cache Misses</span>
              <span className="text-lg font-bold text-orange-600">
                {metrics.hourly.cacheMisses.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Batches Processed</span>
              <span className="text-lg font-bold text-purple-600">
                {metrics.hourly.batchesProcessed.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Performance */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-green-600" />
          Pipeline Performance
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">AI Concurrency</span>
              <div className="text-right">
                <span className="text-lg font-bold text-green-600">
                  {metrics.pipeline.aiConcurrency}
                </span>
                <span className="text-sm text-gray-500 ml-1">/ 50</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Memory Usage</span>
              <div className="text-right">
                <span className="text-lg font-bold text-blue-600">
                  {metrics.pipeline.memoryUsage}
                </span>
                <span className="text-sm text-gray-500 ml-1">MB</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Batch Efficiency</span>
              <div className="text-right">
                <span className="text-lg font-bold text-purple-600">
                  {metrics.pipeline.batchEfficiency}
                </span>
                <span className="text-sm text-gray-500 ml-1">%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Indicators */}
      <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-purple-800">Performance Indicators</h4>
            <div className="mt-2 text-sm text-purple-700 space-y-1">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${hitRateColor === 'text-green-600' ? 'bg-green-500' : hitRateColor === 'text-yellow-600' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                <span>Cache Hit Rate: {metrics.overall.hitRate >= 80 ? 'Excellent' : metrics.overall.hitRate >= 60 ? 'Good' : 'Needs Improvement'}</span>
              </div>
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${metrics.pipeline.batchEfficiency >= 95 ? 'bg-green-500' : metrics.pipeline.batchEfficiency >= 85 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                <span>Batch Efficiency: {metrics.pipeline.batchEfficiency >= 95 ? 'Excellent' : metrics.pipeline.batchEfficiency >= 85 ? 'Good' : 'Needs Improvement'}</span>
              </div>
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${metrics.pipeline.memoryUsage <= 80 ? 'bg-green-500' : metrics.pipeline.memoryUsage <= 95 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                <span>Memory Usage: {metrics.pipeline.memoryUsage <= 80 ? 'Optimal' : metrics.pipeline.memoryUsage <= 95 ? 'Moderate' : 'High'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-center text-xs text-gray-500">
        Last updated: {new Date(metrics.lastUpdated).toLocaleString()}
        <button
          onClick={fetchMetrics}
          className="ml-2 text-purple-600 hover:text-purple-800"
        >
          <RefreshCw className="h-3 w-3 inline" />
        </button>
      </div>
    </div>
  );
}